import { Button } from '@platform/design-system';
import { useTranslation } from '@platform/i18n';
import { LogIn } from 'lucide-react';
import { SessionProgress, type SessionStage } from './SessionProgress.tsx';
import type { SignIn, SignInAttempt } from './use-sign-in.ts';

const FAILURE_KEYS = {
  failed: 'shell.loginFailed',
  insecure: 'shell.insecureContext',
} as const;

const isFailure = (attempt: SignInAttempt): attempt is keyof typeof FAILURE_KEYS => attempt in FAILURE_KEYS;

export type SignInPromptProps = {
  stage: SessionStage;
  titleKey: string;
  descriptionKey: string;
  signIn: SignIn;
};

export const SignInPrompt = ({ stage, titleKey, descriptionKey, signIn }: SignInPromptProps) => {
  const { t } = useTranslation();
  const { attempt } = signIn;
  const failed = isFailure(attempt);

  return (
    <SessionProgress
      stage={stage}
      notice={{
        tone: failed ? 'failure' : 'prompt',
        title: t(failed ? 'shell.loginFailedTitle' : titleKey),
        description: t(failed ? FAILURE_KEYS[attempt] : descriptionKey),
        action: (
          <Button
            icon={<LogIn size={20} aria-hidden="true" />}
            disabled={attempt === 'redirecting'}
            aria-busy={attempt === 'redirecting'}
            onClick={signIn.start}
          >
            {t('shell.login')}
          </Button>
        ),
      }}
    />
  );
};
